import { useState, useEffect, useRef } from 'react';
import { Trash2, Save, X, Download, Paperclip, Bell, Notebook, ShieldAlert } from 'lucide-react';
import Notification from './Notification';
import { fetchData, saveData, updateData, deleteData } from '../utils/dataApi';
import { deleteFile, downloadFileWithAuth, fetchFileBlobUrl, uploadFile } from '../utils/fileApi';
import LegalDocumentsForm from './categoryForms/LegalDocumentsForm';
import ConfirmModal from './ConfirmModal';
import PersonalIdentificationForm from './categoryForms/PersonalIdentificationForm';
import FinancialInformationForm from './categoryForms/FinancialInformationForm';
import PropertyAndAssetsForm from './categoryForms/PropertyAndAssetsForm';
import DigitalPresenceForm from './categoryForms/DigitalPresenceForm';
import KeyContactsForm from './categoryForms/KeyContactsForm';
import FuneralAndBurialWishesForm from './categoryForms/FuneralAndBurialWishesForm';
import HealthcareInformationForm from './categoryForms/HealthcareInformationForm';
import MiscellaneousItemsForm from './categoryForms/MiscellaneousItemsForm';
import Button from './elements/Button';
import Info from './elements/Info';
import Modal from './Modal';
import Input from './elements/Input';
import { isValidEmail } from '../utils/globalUtils';
import FileUpload from './elements/FileUpload';
import AlertManager from './AlertManager';
import { useSearchParams } from 'react-router-dom';
import SubscriptionDetailsModal from './SubscriptionDetailsModal';
import { logout } from '../utils/authApi';

const ManageCategory = ({ selectedCategory, onClose }) => {
  const license = sessionStorage.getItem('license') ? JSON.parse(sessionStorage.getItem('license')) : null;
  const MAX_ALLOWED_FILES = license ? license.record.file.maxCount : 0;
  const MAX_ALLOWED_FILE_SIZE_MB = license ? license.record.file.maxSizeMB : 0;
  const ALLOWED_FILE_TYPES = license ? license.record.file.allowedTypes : [];
  const ALERTS_ALLOWED = license ? license.record.alert : false;

  const [searchParams] = useSearchParams();
  const [role] = useState(sessionStorage.getItem('role'));
  const [notification, setNotification] = useState({ message: '', type: 'success' });
  const [categoryData, setCategoryData] = useState([]);
  const [categoryDataFetchError, setCategoryDataFetchError] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [showAddEditForm, setShowAddEditForm] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [files, setFiles] = useState(Array(MAX_ALLOWED_FILES).fill(null));
  const [formTitle, setFormTitle] = useState('');
  const [formData, setFormData] = useState({});
  const [formError, setFormError] = useState({});
  const [alertData, setAlertData] = useState({ alertType: 'none', alertDay: null, alertMonth: null, alertYear: null, alertMessage: null, alertErrorField: null, alertError: null });
  const [formFiles, setFormFiles] = useState(Array(MAX_ALLOWED_FILES).fill(null));
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [confirmFileDeleteOpen, setConfirmFileDeleteOpen] = useState(false);
  const [pendingDeleteFilename, setPendingDeleteFilename] = useState(null);
  const [confirmAlertClearOpen, setConfirmAlertClearOpen] = useState(false);
  const [confirmUnsavedOpen, setConfirmUnsavedOpen] = useState(false);
  const [pendingUnsavedAction, setPendingUnsavedAction] = useState(null);
  const [attachmentsOpen, setAttachmentsOpen] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [fileUploadKey, setFileUploadKey] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [showSubscriptionDetailsModal, setShowSubscriptionDetailsModal] = useState(false);
  const [recordSearch, setRecordSearch] = useState('');
  const [orderAscending, setOrderAscending] = useState(true);

  const filteredCategoryData = categoryData.filter(record =>
    record.title?.toLowerCase().includes(recordSearch.trim().toLowerCase())
  );

  // Add refs for file inputs
  const fileInputRefs = useRef(Array(MAX_ALLOWED_FILES).fill(null));

  const fileBlobUrlsRef = useRef({});
  const [fileBlobUrls, setFileBlobUrls] = useState({});

  const handleLogout = async () => {
    await logout();
    sessionStorage.clear();
    window.location.href = '/';
  };

  // A named function (not inline in the effect) so the "Retry" action on a
  // record whose content couldn't be decrypted can re-run the same fetch -
  // if reopenRecordId is given and still present (and now decryptable) in
  // the fresh data, it's re-opened automatically instead of leaving the user
  // to find it again in the list.
  const loadCategoryData = async (reopenRecordId) => {
    setIsLoadingData(true);
    setCategoryDataFetchError(false);
    resetForm();
    try {
      const result = await fetchData(selectedCategory.dbCategory);
      if (result.success) {
        setCategoryData(result.data);

        const recordId = reopenRecordId ?? (searchParams.get('id') ? parseInt(searchParams.get('id'), 10) : null);
        if (recordId) {
          const record = result.data.find(r => r.id === recordId);
          if (record) {
            handleEditRecord(record);
          }
        }
      } else {
        setCategoryData([]);
        setCategoryDataFetchError(true);
        setIsLoadingData(false);
        return;
      }
    } catch {
      setCategoryData([]);
      setCategoryDataFetchError(true);
      setIsLoadingData(false);
      return;
    }
    setIsLoadingData(false);
  };

  useEffect(() => {
    if (!selectedCategory) {
      return;
    }
    loadCategoryData();
  }, [selectedCategory]);

  useEffect(() => {
    setRecordSearch('');
  }, [selectedCategory]);

  useEffect(() => {
    setCategoryData(sortByTitle(categoryData));
  }, [orderAscending]);

  useEffect(() => {
    setFormError({ ...formError, title: null });
  }, [formTitle]);

  useEffect(() => {
    setFormError({ ...formError, nomineeName: null });
  }, [formData.nomineeName]);

  useEffect(() => {
    setFormError({ ...formError, nomineeConsentAccepted: null });
  }, [formData.nomineeConsentAccepted]);

  useEffect(() => {
    setFormError({ ...formError, nomineeAccessInfoAccepted: null });
  }, [formData.nomineeAccessInfoAccepted]);

  useEffect(() => {
    let active = true;
    const loadBlobUrls = async () => {
      const jobs = (formFiles || []).map(async (file) => {
        if (!file || typeof file !== 'string') return null;
        const { url } = await fetchFileBlobUrl(file);
        return url ? [file, url] : null;
      });
      const results = (await Promise.all(jobs)).filter(Boolean);
      if (active) {
        Object.values(fileBlobUrlsRef.current).forEach(url => url && URL.revokeObjectURL(url));
        const urls = Object.fromEntries(results);
        fileBlobUrlsRef.current = urls;
        setFileBlobUrls(urls);
      } else {
        results.forEach(([, url]) => URL.revokeObjectURL(url));
      }
    };
    loadBlobUrls();
    return () => { active = false; };
  }, [formFiles]);

  useEffect(() => () => {
    Object.values(fileBlobUrlsRef.current).forEach(url => url && URL.revokeObjectURL(url));
  }, []);

  const sortByTitle = (data) => {
    // A record's title is null when its envelope could not be decrypted (the server
    // sends contentUnavailable alongside). Those sort last in either direction rather
    // than throwing on .toLowerCase() and taking the whole list render down with them.
    const sortedData = [...data].sort((a, b) => {
      const aTitle = typeof a.title === 'string' ? a.title.toLowerCase() : null;
      const bTitle = typeof b.title === 'string' ? b.title.toLowerCase() : null;
      if (aTitle === null && bTitle === null) return 0;
      if (aTitle === null) return 1;
      if (bTitle === null) return -1;
      return orderAscending ? aTitle.localeCompare(bTitle) : bTitle.localeCompare(aTitle);
    });
    return sortedData;
  }

  const parseRecordContent = (rawContent) => {
    try {
      if (rawContent && typeof rawContent === 'object') {
        return rawContent;
      }
      const maybeContent = JSON.parse(rawContent);
      return maybeContent && typeof maybeContent === 'object' ? maybeContent : {};
    } catch {
      return {};
    }
  };

  const getNormalizedFormFiles = (content) => {
    const sourceFiles = Array.isArray(content?.formFiles) ? content.formFiles : [];

    const normalized = Array(MAX_ALLOWED_FILES).fill(null);
    for (let index = 0; index < normalized.length; index++) {
      normalized[index] = typeof sourceFiles[index] === 'string' && sourceFiles[index].trim() ? sourceFiles[index] : null;
    }
    return normalized;
  };

  const hasUnsavedChanges = () => {
    let unsavedChanges = false;
    if (formTitle !== (editRecord && editRecord.title ? editRecord.title : '')) {
      unsavedChanges = true;
    }
    if (JSON.stringify(formData) !== JSON.stringify(editRecord ? parseRecordContent(editRecord.content) : {})) {
      unsavedChanges = true;
    }
    if (JSON.stringify(alertData) !== JSON.stringify({
      alertType: (editRecord ? (editRecord.alertYear ? 'one-time' : (editRecord.alertMonth ? 'yearly' : (editRecord.alertDay ? 'monthly' : 'none'))) : 'none'),
      alertDay: editRecord ? editRecord.alertDay || null : null,
      alertMonth: editRecord ? editRecord.alertMonth || null : null,
      alertYear: editRecord ? editRecord.alertYear || null : null,
      alertMessage: editRecord ? editRecord.alertMessage || null : null,
      alertErrorField: null,
      alertError: null
    })) {
      unsavedChanges = true;
    }
    if (files.some(f => f !== null)) {
      unsavedChanges = true;
    }
    return unsavedChanges;
  }

  const resetForm = () => {
    setShowAddEditForm(false);
    setEditRecord(null);
    setFormTitle('');
    setFormData({});
    setFormError({});
    setAlertData({ alertType: 'none', alertDay: null, alertMonth: null, alertYear: null, alertMessage: null, alertErrorField: null, alertError: null });
    setFormFiles(Array(MAX_ALLOWED_FILES).fill(null));
    setFiles(Array(MAX_ALLOWED_FILES).fill(null));
    setNotification({ message: '', type: 'success' });
    setFileUploadKey(prev => prev + 1);

    // Clear file input values
    fileInputRefs.current.forEach(ref => {
      if (ref) ref.value = '';
    });
  }

  const handleCancel = () => {
    const action = () => {
      resetForm();
      setAttachmentsOpen(false);
      setAlertOpen(false);
    };

    if (hasUnsavedChanges()) {
      setConfirmUnsavedOpen(true);
      setPendingUnsavedAction(() => action);
    } else {
      action();
    }
  };

  const handleOnClose = () => {
    const action = () => {
      resetForm();
      setAttachmentsOpen(false);
      setAlertOpen(false);
      setIsLoadingData(true);
      onClose();
    };

    if (hasUnsavedChanges()) {
      setConfirmUnsavedOpen(true);
      setPendingUnsavedAction(() => action);
    } else {
      action();
    }
  }

  const handleEditRecord = (record) => {
    const action = () => {
      resetForm();
      setAttachmentsOpen(false);
      setAlertOpen(false);
      const parsedContent = parseRecordContent(record.content);
      setEditRecord(record);
      setFormTitle(record.title);
      setFormData(parsedContent);
      // Initialize alert data from record
      setAlertData({
        alertType: record.alertYear ? 'one-time' : (record.alertMonth ? 'yearly' : (record.alertDay ? 'monthly' : 'none')),
        alertDay: record.alertDay || null,
        alertMonth: record.alertMonth || null,
        alertYear: record.alertYear || null,
        alertMessage: record.alertMessage || null,
        alertErrorField: null,
        alertError: null
      });
      setFiles(Array(MAX_ALLOWED_FILES).fill(null));
      setFormFiles(getNormalizedFormFiles(parsedContent));
      setShowAddEditForm(true);
    };

    if (hasUnsavedChanges()) {
      setConfirmUnsavedOpen(true);
      setPendingUnsavedAction(() => action);
    } else {
      action();
    }
  };

  const handleFileChange = (file, index) => {
    const newFiles = [...files];
    newFiles[index] = file || null
    setFiles(newFiles);

    newFiles[index] === null && fileInputRefs.current[index] && (fileInputRefs.current[index].value = '');
  };

  const validateAlert = () => {
    if (alertData.alertError) {
      setAlertData({ ...alertData, alertErrorField: alertData.alertErrorField, alertError: alertData.alertError });
      return false;
    }

    if (alertData.alertType === 'none') {
      // No alert set, so it's valid
      setAlertData({ ...alertData, alertErrorField: null, alertError: null });
      return true;
    }

    // Day validations
    if ((alertData.alertType === 'one-time' || alertData.alertType === 'yearly' || alertData.alertType === 'monthly') && alertData.alertDay === null) {
      setAlertData({ ...alertData, alertErrorField: 'alertDay', alertError: `Day is required for ${alertData.alertType} alerts` });
      return false;
    }
    if (alertData.alertDay !== null && (alertData.alertDay < 1 || alertData.alertDay > 31)) {
      setAlertData({ ...alertData, alertErrorField: 'alertDay', alertError: 'Day must be between 1 and 31' });
      return false;
    }

    // Month validations
    if ((alertData.alertType === 'one-time' || alertData.alertType === 'yearly') && alertData.alertMonth === null) {
      setAlertData({ ...alertData, alertErrorField: 'alertMonth', alertError: `Month is required for ${alertData.alertType} alerts` });
      return false;
    }
    if (alertData.alertMonth !== null && (alertData.alertMonth < 1 || alertData.alertMonth > 12)) {
      setAlertData({ ...alertData, alertErrorField: 'alertMonth', alertError: 'Month must be between 1 and 12' });
      return false;
    }

    // Year validations
    if (alertData.alertType === 'one-time' && alertData.alertYear === null) {
      setAlertData({ ...alertData, alertErrorField: 'alertYear', alertError: `Year is required for ${alertData.alertType} alerts` });
      return false;
    }

    // Future date validation for one-time alerts
    if (alertData.alertType === 'one-time' && alertData.alertDay !== null && alertData.alertMonth !== null && alertData.alertYear !== null) {
      const alertDate = new Date(alertData.alertYear, alertData.alertMonth - 1, alertData.alertDay);
      const now = new Date();
      if (alertDate <= now) {
        setAlertData({ ...alertData, alertErrorField: 'alertDate', alertError: 'Date must be in the future for one-time alerts' });
        return false;
      }
    }

    // Message validation
    if (alertData.alertDay !== null && alertData.alertMessage === null) {
      setAlertData({ ...alertData, alertErrorField: 'alertMessage', alertError: 'Message is required when an Alert is set' });
      return false;
    }

    if (alertData.alertMessage !== null && (alertData.alertMessage.trim() === '' || alertData.alertMessage.length > 500)) {
      setAlertData({ ...alertData, alertErrorField: 'alertMessage', alertError: 'Message must be between 1 and 500 characters' });
      return false;
    }

    setAlertData({ ...alertData, alertErrorField: null, alertError: null });
    return true;
  };

  const validateForm = () => {
    setNotification({ message: '', type: 'success' });
    const titleField = 'Title';

    if (!formTitle || formTitle.trim() === '') {
      setFormError({ ...formError, title: `${titleField} is required` });
      return false;
    }
    // Same null-title case as sortByTitle: a record that failed to decrypt has no title
    // to compare, and cannot collide with anything the user types.
    if (categoryData.some(r => typeof r.title === 'string'
      && r.title.trim().toLowerCase() === formTitle.trim().toLowerCase()
      && (!editRecord || r.id !== editRecord.id))) {
      setFormError({ ...formError, title: `A record with this ${titleField.toLowerCase()} already exists` });
      return false;
    }


    for (const [key, value] of [['title', formTitle], ...Object.entries(formData)]) {
      let allowedLength = key === 'notes' ? 1000 : 100; // Default allowed length

      if (typeof value === 'string' && value.length > allowedLength) {
        setFormError({ ...formError, [key]: `Value must not exceed ${allowedLength} characters` });
        setAttachmentsOpen(false);
        setAlertOpen(false);
        return false;
      }
    }

    if (formData.email && !isValidEmail(formData.email)) {
      setFormError({ ...formError, email: 'Invalid email format' });
      setAttachmentsOpen(false);
      setAlertOpen(false);
      return false;
    }

    if (validateAlert() === false) {
      setAlertOpen(true);
      setAttachmentsOpen(false);
      return false;
    }
    
    return true;
  };

  // uploadedFiles (returned) is formFiles merged with whatever new uploads
  // succeeded this call - the full list a record should be saved with.
  // newlyUploadedFiles is the strict subset actually uploaded just now, kept
  // separate so a caller can revert *only* those on a later failure, never a
  // record's pre-existing attachments (see revertUploadedFiles).
  const uploadFiles = async () => {
    const uploadedFiles = [...formFiles];
    const newlyUploadedFiles = Array(MAX_ALLOWED_FILES).fill(null);
    let fileErrors = Array(MAX_ALLOWED_FILES).fill(null);
    for (let index = 0; index < files.length; index++) {
      const file = files[index];
      if (file) {
        try {
          const uploadedFile = await uploadFile(file);
          if (uploadedFile.success) {
            uploadedFiles[index] = uploadedFile.filename;
            newlyUploadedFiles[index] = uploadedFile.filename;
          } else {
            fileErrors[index] = uploadedFile.message || 'File upload error';
          }
        } catch {
          fileErrors[index] = 'File upload error';
        }
      }
    }

    setFormFiles(uploadedFiles);
    return { uploadedFiles, newlyUploadedFiles, fileErrors };
  };

  // Deletes only files uploaded during the just-failed save attempt
  // (newlyUploadedFiles from uploadFiles() above) - a record's pre-existing
  // attachments must never be touched by a save that failed to persist any
  // change to them. Previously this took the full merged list and deleted
  // every entry in it, including attachments the record already had before
  // this edit even started - editing just a title, with attachments left
  // alone, and hitting any save failure silently deleted the record's real
  // files. Restores formFiles to its pre-attempt snapshot rather than
  // blanking it, so the still-open form keeps showing what's actually saved.
  const revertUploadedFiles = async (newlyUploadedFiles, formFilesBeforeUpload) => {
    for (let index = 0; index < newlyUploadedFiles.length; index++) {
      const file = newlyUploadedFiles[index];

      if (file) {
        try {
          await deleteFile(file);
        } catch {
          // Handle error if needed
        }
      }
    }
    setFormFiles(formFilesBeforeUpload);
  };

  const buildRecordContentJson = (uploadedFiles) => {
    const payload = { ...formData, formFiles: uploadedFiles };

    return JSON.stringify(payload);
  };

  const handleAddRecord = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    const formFilesBeforeUpload = formFiles;
    let fileRevertList = [];

    setIsSaving(true);
    try {
      const { uploadedFiles, newlyUploadedFiles, fileErrors } = await uploadFiles();
      fileRevertList = newlyUploadedFiles;
      const contentJson = buildRecordContentJson(uploadedFiles);

      const result = await saveData(selectedCategory.dbCategory,
        formTitle.trim(),
        contentJson,
        alertData.alertDay,
        alertData.alertMonth,
        alertData.alertYear,
        alertData.alertMessage
      );
      if (result.success) {
        setCategoryData(sortByTitle([...categoryData, { id: result.id, title: formTitle.trim(), content: contentJson, alertDay: alertData.alertDay, alertMonth: alertData.alertMonth, alertYear: alertData.alertYear, alertMessage: alertData.alertMessage }]));
        if (fileErrors.some(error => error !== null)) {
          setAttachmentsOpen(true);
          setAlertOpen(false);
          setNotification({ message: 'Record added, but some files were not uploaded successfully.', type: 'error' });
        } else {
          resetForm();
          setNotification({ message: 'Record added successfully!', type: 'success' });
        }
      } else {
        setNotification({ message: result.message || 'Failed to add record. Please try again later.', type: 'error' });
        await revertUploadedFiles(fileRevertList, formFilesBeforeUpload);
      }
    } catch {
      setNotification({ message: 'Failed to add record. Please try again later.', type: 'error' });
      await revertUploadedFiles(fileRevertList, formFilesBeforeUpload);
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateRecord = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    const formFilesBeforeUpload = formFiles;
    let fileRevertList = [];

    setIsSaving(true);
    try {
      const { uploadedFiles, newlyUploadedFiles, fileErrors } = await uploadFiles();
      fileRevertList = newlyUploadedFiles;
      const contentJson = buildRecordContentJson(uploadedFiles);

      const result = await updateData(editRecord.id,
        selectedCategory.dbCategory,
        formTitle.trim(),
        contentJson,
        alertData.alertDay,
        alertData.alertMonth,
        alertData.alertYear,
        alertData.alertMessage
      );
      if (result.success) {
        setCategoryData(sortByTitle(categoryData.map(r => r.id === editRecord.id ? { ...r, title: formTitle.trim(), content: contentJson, alertDay: alertData.alertDay, alertMonth: alertData.alertMonth, alertYear: alertData.alertYear, alertMessage: alertData.alertMessage } : r)));
        if (fileErrors.some(error => error !== null)) {
          setAttachmentsOpen(true);
          setAlertOpen(false);
          setNotification({ message: `Record updated, but some files were not uploaded successfully. ${fileErrors.filter(Boolean).join(', ')}`, type: 'error' });
        } else {
          resetForm();
          setNotification({ message: 'Record updated successfully!', type: 'success' });
        }
      } else {
        setNotification({ message: result.message || 'Failed to update record. Please try again later.', type: 'error' });
        await revertUploadedFiles(fileRevertList, formFilesBeforeUpload);
      }
    } catch {
      setNotification({ message: 'Failed to update record. Please try again later.', type: 'error' });
      await revertUploadedFiles(fileRevertList, formFilesBeforeUpload);
    } finally {
      setIsSaving(false);
    }
  };

  // downloadFileWithAuth previously failed completely silently on any error -
  // clicking Download while Vault was unreachable did nothing visible at
  // all, with no toast, no message, nothing. Surface a real notification,
  // distinguishing the Vault-unavailable case so the message is accurate.
  const handleDownloadFile = async (filename) => {
    let result;
    try {
      result = await downloadFileWithAuth(filename);
    } catch {
      // A rejected fetch (offline, aborted, TLS failure) throws rather than
      // resolving { success: false } - without this catch that's exactly the
      // silent "nothing happens" failure this wrapper exists to fix.
      result = { success: false, code: null };
    }
    if (!result.success) {
      setNotification({
        message: result.code === 'VAULT_UNAVAILABLE'
          ? "We're having trouble accessing secure storage right now. Your file is safe - please try again in a few minutes."
          : 'Failed to download file. Please try again later.',
        type: 'error',
      });
    }
  };

  const handleClearAlert = () => {
    setAlertData({ alertType: 'none', alertDay: null, alertMonth: null, alertYear: null, alertMessage: null, alertErrorField: null, alertError: null });
    setConfirmAlertClearOpen(false);
  };

  const handleDeleteFile = async (filename) => {
    let deleteResult = null;
    let contentJson = null;

    try {
      deleteResult = await deleteFile(filename);

      if (deleteResult.success) {
        setNotification({ message: 'File deleted successfully!', type: 'success' });
        const updatedFormFiles = formFiles.map(f => f === filename ? null : f);
        setFormFiles(updatedFormFiles);
        contentJson = JSON.stringify({ ...formData, formFiles: updatedFormFiles });
        setPendingDeleteFilename(null);
      } else {
        setNotification({ message: deleteResult.message || 'Failed to delete file. Please try again later.', type: 'error' });
      }
    } catch {
      setNotification({ message: 'Failed to delete file. Please try again later.', type: 'error' });
    }

    if (deleteResult.success) {
      try {
        const result = await updateData(editRecord.id, selectedCategory.dbCategory, formTitle.trim(), contentJson);
        if (result.success) {
          setCategoryData(categoryData.map(r => r.id === editRecord.id ? { ...r, title: formTitle.trim(), content: contentJson, alertDay: alertData.alertDay, alertMonth: alertData.alertMonth, alertYear: alertData.alertYear, alertMessage: alertData.alertMessage } : r));
          setNotification({ message: 'Record updated successfully!', type: 'success' });
        } else {
          setNotification({ message: result.message || 'Failed to update record. Please try again later.', type: 'error' });
        }
      } catch {
        setNotification({ message: 'Failed to update record. Please try again later.', type: 'error' });
      }
    }
  };

  const handleDeleteRecord = async (id) => {
    try {
      await Promise.all(formFiles.map(file => {
        if (file) {
          return handleDeleteFile(file);
        }
      }));

      const result = await deleteData(id);
      if (result.success) {
        setCategoryData(categoryData.filter(r => r.id !== id));
        resetForm();
        setNotification({ message: 'Record deleted successfully!', type: 'success' });
      } else {
        setNotification({ message: result.message || 'Failed to delete record. Please try again later.', type: 'error' });
      }
    } catch {
      setNotification({ message: 'Failed to delete record. Please try again later.', type: 'error' });
    }
  };

  if (isLoadingData && selectedCategory) {
    return null;
  }

  return (
    <>
      {selectedCategory && (
        <Modal open={selectedCategory !== null} onClose={handleOnClose} icon={selectedCategory.icon} title={`Manage ${selectedCategory.title}`} className='w-full'>
          <Notification message={notification.message} onClose={() => setNotification({ message: '', type: 'success' })} type={notification.type} />
          {categoryDataFetchError ? (
            <Info type="error" className="w-full" textClassName="text-center">
              Failed to load data. Please try again later.
            </Info>
          ) : (
            <>
              <div className={`sm:w-1/4 w-full border-accent-main sm:border-r sm:pr-4 sm:block ${editRecord ? 'hidden' : ''}`}>
                <div className='flex mb-2 gap-2 items-center'>
                  <h4 className="flex-1 font-semibold text-lg text-primary-dark">Records</h4>
                  <Button type="primary_outline" size="small" onClick={() => { setOrderAscending(!orderAscending); }} className="min-w-[10px]">
                    {orderAscending ? 'A-Z' : 'Z-A'}
                  </Button>
                </div>
                <div className="mb-2 sm:hidden">
                  <Button type='primary' onClick={() => { resetForm(); setEditRecord({}); setShowAddEditForm(true); }} className="min-w-full">
                    Add New Record
                  </Button>
                </div>
                {categoryData.length === 0 && <p className="text-primary-dark">No records found.</p>}
                {categoryData.length > 0 && (
                  <>
                    <Input
                      type="text"
                      value={recordSearch}
                      onChange={e => setRecordSearch(e.target.value)}
                      placeholder="Search by title"
                    />
                    {filteredCategoryData.length === 0 && <p className="text-primary-dark">No matching records found.</p>}
                  </>
                )}

                <div className='h-[50vh]' style={{ overflowY: 'auto' }}>
                  <ul>
                    {filteredCategoryData.map(record => (
                      <li key={record.id} className="mb-2">
                        <a
                          href="#"
                          className={`block text-primary-dark border border-accent-main rounded-lg px-3 py-2 hover:bg-accent-main/50 hover:border-primary-dark overflow-hidden ${editRecord && editRecord.id === record.id ? 'bg-accent-main' : 'bg-background-light'}`}
                          onClick={e => { e.preventDefault(); handleEditRecord(record); }}
                        >
                          <span title={record.title}>{record.title}</span>
                        </a>
                      </li>
                    ))}
                    
                  </ul>
                </div>
              </div>
              {!showAddEditForm && (
                <div className="flex-1 sm:block sm:pl-4 rounded-lg border-2 border-dashed border-accent-main p-4 hidden">
                  <div className="mb-4">
                    <h4 className="font-semibold text-lg text-primary-dark">Record Details</h4>
                  </div>
                  <div className="flex flex-col items-center justify-center text-center gap-4 h-[40vh]">
                    <p className="text-primary-dark">Select a record to view details or add a new record.</p>
                    <Button
                      type='primary'
                      onClick={() => { resetForm(); setEditRecord({}); setShowAddEditForm(true); }}
                    >
                      Add New Record
                    </Button>
                  </div>
                </div>
              )}
              <div className={`flex-1 ${showAddEditForm ? 'visible' : 'hidden'}`}>
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-semibold text-lg text-primary-dark">{editRecord && Object.keys(editRecord).length ? 'Edit Record' : 'Add New Record'}</h4>
                    {/* Hidden alongside the panels it toggles when the record is locked -
                        otherwise these stay clickable while switching to nothing (the panels
                        are gone), and Attachments always reads "0 Attachment(s)" since
                        formFiles was never populated for a record whose content never loaded. */}
                    {!editRecord?.contentUnavailable && (
                    <span className="text-primary-dark flex items-center gap-2">
                      <Button
                        type={!alertOpen && !attachmentsOpen ? 'primary' : 'primary_outline'}
                        onClick={() => { setAlertOpen(false); setAttachmentsOpen(false); }}
                        className="sm:min-w-[100px] min-w-[10px]"
                      >
                        <Notebook className="w-4 h-4" />
                        <span className="ml-1 hidden sm:inline">Details</span>
                      </Button>
                      {role !== 'nominee' && (
                        <Button
                          type={alertOpen ? 'primary' : 'primary_outline'}
                          onClick={() => { setAlertOpen(true); setAttachmentsOpen(false); }}
                          className="sm:min-w-[100px] min-w-[10px]"
                        >
                          <Bell className="w-4 h-4" />
                          <span className="ml-1 hidden sm:inline">Alert</span>
                        </Button>
                      )}
                      <Button
                        type={attachmentsOpen ? 'primary' : 'primary_outline'}
                        onClick={() => { setAttachmentsOpen(true); setAlertOpen(false); }}
                        className="sm:min-w-[100px] min-w-[10px]"
                      >
                        <Paperclip className="w-4 h-4" />
                        <span className="ml-1 hidden sm:inline">{`${formFiles.filter(f => f).length} Attachment(s)`}</span>
                      </Button>
                    </span>
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    {editRecord?.contentUnavailable && (
                      <Info type="warning" className="w-full">
                        <p className="flex items-center gap-2 font-semibold">
                          <ShieldAlert className="w-4 h-4 flex-shrink-0" /> This record&apos;s content couldn&apos;t be loaded.
                        </p>
                        {editRecord.contentUnavailable === 'VAULT_UNAVAILABLE' ? (
                          <p>This is a temporary issue — nothing has been lost. Editing is disabled until it&apos;s back, so nothing risks being saved over the real content.</p>
                        ) : (
                          <p>Your saved content is still stored safely, but we couldn&apos;t read it back. Editing is disabled so nothing risks being saved over it. Please contact support.</p>
                        )}
                        {editRecord.contentUnavailable === 'VAULT_UNAVAILABLE' && (
                          <div className="flex justify-center mt-2">
                            <Button type="primary" onClick={() => loadCategoryData(editRecord.id)}>Retry</Button>
                          </div>
                        )}
                      </Info>
                    )}
                    {!editRecord?.contentUnavailable && (
                    <>
                    <Input
                      label="Title"
                      type="text"
                      required
                      value={formTitle}
                      onChange={e => setFormTitle(e.target.value)}
                      placeholder="Enter a unique name to identify this record"
                      error={formError.title}
                    />

                    {(!attachmentsOpen && !alertOpen) && (
                      <div>
                        {/* Dynamic category form */}
                        {selectedCategory && selectedCategory.dbCategory === 'LEGAL_DOCUMENTS' && (
                          <LegalDocumentsForm formData={formData} setFormData={setFormData} formError={formError} />
                        )}
                        {selectedCategory && selectedCategory.dbCategory === 'PERSONAL_IDENTIFICATION' && (
                          <PersonalIdentificationForm formData={formData} setFormData={setFormData} formError={formError} />
                        )}
                        {selectedCategory && selectedCategory.dbCategory === 'FINANCIAL_INFORMATION' && (
                          <FinancialInformationForm formData={formData} setFormData={setFormData} formError={formError} />
                        )}
                        {selectedCategory && selectedCategory.dbCategory === 'PROPERTY_AND_ASSETS' && (
                          <PropertyAndAssetsForm formData={formData} setFormData={setFormData} formError={formError} />
                        )}
                        {selectedCategory && selectedCategory.dbCategory === 'DIGITAL_PRESENCE' && (
                          <DigitalPresenceForm formData={formData} setFormData={setFormData} formError={formError} />
                        )}
                        {selectedCategory && selectedCategory.dbCategory === 'KEY_CONTACTS' && (
                          <KeyContactsForm formData={formData} setFormData={setFormData} formError={formError} />
                        )}
                        {selectedCategory && selectedCategory.dbCategory === 'FUNERAL_AND_BURIAL_WISHES' && (
                          <FuneralAndBurialWishesForm formData={formData} setFormData={setFormData} formError={formError} />
                        )}
                        {selectedCategory && selectedCategory.dbCategory === 'HEALTHCARE_INFORMATION' && (
                          <HealthcareInformationForm formData={formData} setFormData={setFormData} formError={formError} />
                        )}
                        {selectedCategory && selectedCategory.dbCategory === 'MISCELLANEOUS_ITEMS' && (
                          <MiscellaneousItemsForm formData={formData} setFormData={setFormData} formError={formError} />
                        )}
                      </div>
                    )}

                    {/* Alert Manager - toggle visibility with alertOpen */}
                    {(!attachmentsOpen && alertOpen) && (
                      <div className="mb-4">
                        {role !== 'nominee' && alertOpen && (
                          <>
                            {!ALERTS_ALLOWED ? (
                              <>
                                <div className="flex items-center justify-between mb-2 border-b border-gray-300 pb-1">
                                  <div className="flex items-center gap-2 mb-2">
                                    <Bell className="w-5 h-5 text-accent-main" />
                                    <h3 className="text-lg font-semibold text-primary-dark">Alert & Reminder</h3>
                                  </div>
                                </div>
                                <Info type="warning" className="mb-2">
                                  <p>You do not have permission to set alerts. Please upgrade your plan to enable this feature.</p>
                                  <div className="flex justify-center mt-2">
                                    <Button type="primary" onClick={() => setShowSubscriptionDetailsModal(true)}>
                                      View Plans
                                    </Button>
                                  </div>
                                </Info>
                              </>
                            ) : (
                              <AlertManager
                                alert={alertData}
                                onAlertChange={(newAlertData) => setAlertData(newAlertData)}
                                onClear={() => setConfirmAlertClearOpen(true)}
                              />
                            )}
                          </>
                        )}
                      </div>
                    )}

                    {(attachmentsOpen && !alertOpen) && (
                      <div className="mb-4">
                        <div className="flex items-center justify-between mb-2 border-b border-gray-300 pb-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Paperclip className="w-5 h-5 text-accent-main" />
                            <h3 className="text-lg font-semibold text-primary-dark">Attachments</h3>
                          </div>
                        </div>
                        <div className="flex flex-col gap-2">
                          {MAX_ALLOWED_FILES === 0 && (
                            <Info type="warning">
                              <p>You do not have permission to upload attachments. Please upgrade your plan to enable this feature.</p>
                              <div className="flex justify-center mt-2">
                                <Button type="primary" onClick={() => setShowSubscriptionDetailsModal(true)}>
                                  View Plans
                                </Button>
                              </div>
                            </Info>
                          )}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {(MAX_ALLOWED_FILES > 0) && formFiles && formFiles.map((file, idx) => (
                              <div key={idx} className="flex flex-col items-center border border-accent-main rounded p-2 w-full bg-background-light">
                                {file ? (
                                  <>
                                    <div className="w-full flex mb-1 h-6">
                                      <span className="text-sm text-primary-dark mr-2">{file.split('_').splice(1).join('_')}</span>
                                      <div className="flex ml-auto">
                                        <Button
                                          type="primary"
                                          size="small"
                                          className="min-w-4"
                                          onClick={() => handleDownloadFile(file)}
                                          error={formError && formError.files && formError.files[idx]}
                                          title="Download file"
                                        >
                                          <Download className="w-4 h-4" />
                                        </Button>
                                        {role !== 'nominee' && (
                                          <Button
                                            type="danger"
                                            size="small"
                                            className="min-w-4 ml-2"
                                            onClick={() => { setPendingDeleteFilename(file); setConfirmFileDeleteOpen(true); }}
                                            title="Delete file"
                                          >
                                            <Trash2 className="w-4 h-4" />
                                          </Button>
                                        )}
                                      </div>
                                    </div>

                                    <div className="h-48">
                                      {(file.endsWith('.jpg') || file.endsWith('.jpeg') || file.endsWith('.png') || file.endsWith('.gif')) ? (
                                        <a href={fileBlobUrls[file] || '#'} target='_blank' title="Preview file">
                                          <img className="w-full h-full" src={fileBlobUrls[file] || ''} alt="Attachment preview" />
                                        </a>
                                      ) : (file.endsWith('.mp4') || file.endsWith('.mov') || file.endsWith('.wav') || file.endsWith('.mp3')) ? (
                                        <video className="w-full h-full" controls>
                                          <source src={fileBlobUrls[file] || ''} type="video/mp4" />
                                          Your browser does not support the video tag.
                                        </video>
                                      ) : (
                                        <iframe
                                          src={fileBlobUrls[file] || ''}
                                          title={file}
                                          className="w-full h-full border-none"
                                        />
                                      )}
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    <div className="w-full h-48">
                                      {role === 'nominee' ? (
                                        <div className="w-full h-full flex flex-col items-center justify-center">
                                          <span className="text-sm text-primary-dark mt-3">No file uploaded.</span>
                                        </div>
                                      ) : (
                                        <FileUpload
                                          key={`file-upload-${idx}-${fileUploadKey}`}
                                          label="Upload Document"
                                          accept={ALLOWED_FILE_TYPES.join(', ')}
                                          maxSize={MAX_ALLOWED_FILE_SIZE_MB * 1024 * 1024}
                                          onFileSelect={(file) => handleFileChange(file, idx)}
                                        />
                                      )}
                                    </div>
                                    <div className="w-full h-7 flex items-center justify-center">

                                    </div>
                                  </>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                    </>
                    )}

                    {role !== 'nominee' && (
                      <div className="flex gap-2 mt-2 items-center">
                        {editRecord?.contentUnavailable ? (
                          <>
                            <Button type='secondary' onClick={handleCancel} className="sm:min-w-[100px] min-w-[10px]" title="Cancel">
                              <span className="hidden sm:inline">Cancel</span>
                              <X className="sm:hidden w-5 h-5" />
                            </Button>
                            {/* Editing is blocked (content can't be verified before a save
                                would overwrite it), but a record that can never be decrypted -
                                Vault access permanently revoked, min_decryption_version raised
                                past its key, or a corrupted envelope - still has to be
                                removable, or it's stuck in the vault forever with no affordance
                                to clear it. Deleting doesn't require reading the content first. */}
                            <div className="flex-1 flex justify-end">
                              <Button type='danger' onClick={() => { setPendingDeleteId(editRecord.id); setConfirmOpen(true); }} className="sm:min-w-[100px] min-w-[10px]" title="Delete record">
                                <span className="hidden sm:inline">Delete</span>
                                <Trash2 className="sm:hidden w-5 h-5" />
                              </Button>
                            </div>
                          </>
                        ) : editRecord && Object.keys(editRecord).length ? (
                          <>
                            <Button type='secondary' onClick={handleCancel} className="sm:min-w-[100px] min-w-[10px]" title="Cancel">
                              <span className="hidden sm:inline">Cancel</span>
                              <X className="sm:hidden w-5 h-5" />
                            </Button>

                            <div className="flex-1 flex justify-end gap-2">
                              <Button type='danger' onClick={() => { setPendingDeleteId(editRecord.id); setConfirmOpen(true); }} className="sm:min-w-[100px] min-w-[10px]" title="Delete record">
                                <span className="hidden sm:inline">Delete</span>
                                <Trash2 className="sm:hidden w-5 h-5" />
                              </Button>
                              <Button type='primary' onClick={handleUpdateRecord} loading={isSaving} className="sm:min-w-[100px] min-w-[10px]" title="Update record">
                                <span className="hidden sm:inline">Update</span>
                                <Save className="sm:hidden w-5 h-5" />
                              </Button>
                            </div>
                          </>
                        ) : (
                          <>
                            <Button type='secondary' onClick={handleCancel} className="sm:min-w-[100px] min-w-[10px]" title="Cancel">
                              <span className="hidden sm:inline">Cancel</span>
                              <X className="sm:hidden w-5 h-5" />
                            </Button>
                            <div className="flex-1 flex justify-end">
                              <Button type='primary' onClick={handleAddRecord} loading={isSaving} className="sm:min-w-[100px] min-w-[10px]" title="Add record">
                                <span className="hidden sm:inline">Add</span>
                                <Save className="sm:hidden w-5 h-5" />
                              </Button>
                            </div>
                          </>
                        )}
                      </div>
                    )}

                    <ConfirmModal
                      open={confirmOpen}
                      title="Delete Record"
                      message="Are you sure you want to delete this record? This action cannot be undone."
                      onCancel={() => { setConfirmOpen(false); setPendingDeleteId(null); }}
                      onConfirm={() => {
                        handleDeleteRecord(pendingDeleteId);
                        setConfirmOpen(false);
                        setPendingDeleteId(null);
                      }}
                    />

                    <ConfirmModal
                      open={confirmFileDeleteOpen}
                      title="Delete File"
                      message="Are you sure you want to delete this file? This action cannot be undone."
                      onCancel={() => { setConfirmFileDeleteOpen(false); }}
                      onConfirm={() => {
                        handleDeleteFile(pendingDeleteFilename);
                        setConfirmFileDeleteOpen(false);
                      }}
                    />

                    <ConfirmModal
                      open={confirmAlertClearOpen}
                      title="Clear Alert"
                      message="Are you sure you want to remove this alert?"
                      onCancel={() => { setConfirmAlertClearOpen(false); }}
                      onConfirm={handleClearAlert}
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </Modal>
      )}

      <SubscriptionDetailsModal
        open={showSubscriptionDetailsModal}
        onClose={() => setShowSubscriptionDetailsModal(false)}
        onCancelSubscription={handleLogout}
      />

      <ConfirmModal
        open={confirmUnsavedOpen}
        title="Unsaved Changes"
        message="You have unsaved changes. If you discard now, all your edits will be lost. Do you want to continue editing or discard your changes?"
        cancelText="Continue Editing"
        confirmText="Discard Changes"
        onCancel={() => {
          setConfirmUnsavedOpen(false);
          setPendingUnsavedAction(null);
        }}
        onConfirm={() => {
          setConfirmUnsavedOpen(false);
          if (pendingUnsavedAction) {
            const fn = pendingUnsavedAction();
            if (typeof fn === 'function') fn();
            setPendingUnsavedAction(null);
          }
        }}
      />
    </>
  );
};

export default ManageCategory;

